import React, { useCallback, useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AArrowDown, AArrowUp, ArrowDownWideNarrow, ArrowUpNarrowWide, Filter, Eye, ScanBarcode, Check } from "lucide-react";
import {
  listAllGiftCardRedemption,
  listAllWithdrawalRequest,
  makePayPalPayment,
} from "../../../reducers/payouts.slice";
import { convertDateToCT, formatNumberToDollar, countActiveFiltersAndSort } from "../../../utils/util";
import { getUsers } from "../../../utils/getUser";
import { AuthContext } from "../../../context/authContext";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
  DropdownMenuItem,
  DropdownMenuLabel,
} from "../../../components/ui/dropdown-menu";
import StatusBadge from "../../../components/shared/StatusBadge";
import DataTable from "../../../components/shared/DataTable";
import PageHeader from "../../../components/shared/PageHeader";
import RefreshButton from "../../../components/shared/RefreshButton";
import SearchBar from "../../../components/shared/SearchBar";
import useMediaQuery from "../../../components/shared/useMediaQuery";
import UpdatePayoutFormPage from "../EditPayoutPage";
import GiftCardRedemptionPage from "../GiftCardRedemptionPage";
import ViewWithdrawRequestPage from "../ViewWithdrawRequest";

const WITHDRAW_STATUS_LABEL = {
  PENDING: "Pending",
  PENDING_APPROVAL: "Pending",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  READY_FOR_REDEMPTION: "Ready for Redemption",
  PROCESSING: "Processing",
  ACTION_REQUIRED: "Action Required",
  FAILED: "Failed",
};

const ManagementWithdrawRequestPage = () => {
  const dispatch = useDispatch();
  const user = getUsers();

  const [loading, setLoading] = useState(false);
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [requestType, setRequestType] = useState(() => {
    return sessionStorage.getItem("requestType") || "withdraw";
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const s = sessionStorage.getItem("withdrawRequest_selectedStatuses");
    return s ? JSON.parse(s) : [];
  });
  const [sortDirection, setSortDirection] = useState(() => {
    return sessionStorage.getItem("withdrawRequest_sortDirection") || null;
  });
  const [selectedRows, setSelectedRows] = useState([]);
  const [resetSelectionFlag, setResetSelectionFlag] = useState(false);
  const [openPayPalPayment, setOpenPayPalPayment] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewRequest, setViewRequest] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [redemptionRequest, setRedemptionRequest] = useState(null);

  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const maskSensitiveNumber = (number) => {
    if (!number) return "****";
    const lastFour = number.slice(-4);
    return "*".repeat(Math.max(0, number.length - 4)) + lastFour;
  };

  const formatRetailerName = (retailer) => retailer?.replace(/_/g, " ");

  const getPaymentDetails = (changedRequestType) => {
    setLoading(true);
    const newRequestType = changedRequestType || requestType;
    const api =
      newRequestType === "withdraw" ? listAllWithdrawalRequest : listAllGiftCardRedemption;
    dispatch(api()).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setLoading(false);
        showToast(res?.error?.message, "error");
        return;
      }
      const formatted = res?.payload?.map((item) => {
        const createDate = convertDateToCT(item?.created_at, "MMMM D, YYYY hh:mm:ss A");
        const fullName = `${item?.user_details?.first_name} ${item?.user_details?.last_name}`;
        const description =
          newRequestType === "withdraw"
            ? item?.payment_method_details?.payment_method === "ACH"
              ? `Bank Transfer - ${item?.payment_method_details?.account_last_four ? `****${item.payment_method_details.account_last_four}` : maskSensitiveNumber(item?.payment_method_details?.account_number)}`
              : item?.payment_method_details?.payment_method === "PayPal"
                ? "PayPal"
                : "NA"
            : formatRetailerName(item?.gift_card_details?.retailer);
        const actual_amount =
          newRequestType === "withdraw"
            ? formatNumberToDollar(item?.amount)
            : formatNumberToDollar(item?.actual_amount);
        const requested_date = convertDateToCT(item?.created_at, "MMMM D, YYYY hh:mm:ss A");
        const paymentMethod =
          item?.payment_method_details?.payment_method === "ACH"
            ? "ACH"
            : item?.payment_method_details?.payment_method === "PayPal"
              ? "PayPal"
              : "NA";
        const accountDetails = item?.payment_method_details?.account_last_four
          ? `****${item.payment_method_details.account_last_four}`
          : maskSensitiveNumber(item?.payment_method_details?.account_number);
        const paymentDate = convertDateToCT(item?.payment_date, "MMMM D, YYYY hh:mm:ss A");
        const isPayPalSelectable =
          paymentMethod === "PayPal" &&
          (item?.status === "PENDING" || item?.status === "ACTION_REQUIRED");
        return {
          ...item,
          createDate,
          fullName,
          actual_amount,
          requested_date,
          description,
          paymentMethod,
          accountDetails,
          paymentDate,
          isPayPalSelectable,
        };
      });
      setLoading(false);
      setAllData(formatted || []);
    });
  };

  useEffect(() => {
    getPaymentDetails();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    let result = [...allData];
    const q = debouncedSearch.trim().toLowerCase();
    if (q) {
      result = result.filter(
        (item) =>
          item?.id?.toString().toLowerCase().includes(q) ||
          item?.fullName?.toLowerCase().includes(q)
      );
    }
    if (selectedStatuses.length > 0) {
      result = result.filter((item) => selectedStatuses.includes(item?.status));
    }
    if (sortDirection === "asc") {
      result.sort((a, b) => Number(a?.amount || 0) - Number(b?.amount || 0));
    } else if (sortDirection === "desc") {
      result.sort((a, b) => Number(b?.amount || 0) - Number(a?.amount || 0));
    }
    setFilteredData(result);
    setCurrentPage(0);
  }, [allData, debouncedSearch, selectedStatuses, sortDirection]);

  useEffect(() => {
    sessionStorage.setItem("withdrawRequest_selectedStatuses", JSON.stringify(selectedStatuses));
  }, [selectedStatuses]);

  useEffect(() => {
    if (sortDirection) sessionStorage.setItem("withdrawRequest_sortDirection", sortDirection);
    else sessionStorage.removeItem("withdrawRequest_sortDirection");
  }, [sortDirection]);

  const handleChangeRequestType = (value) => {
    setRequestType(value);
    sessionStorage.setItem("requestType", value);
    getPaymentDetails(value);
    setSelectedStatuses([]);
    setSortDirection(null);
    setCurrentPage(0);
    setSearchTerm("");
    setDebouncedSearch("");
  };

  const handleViewWithdrawRequest = (row) => {
    setViewRequest(row);
  };

  const columns = [
    { key: "id", label: "ID" },
    { key: "fullName", label: "Member" },
    { key: "paymentMethod", label: "Type" },
    { key: "description", label: "Description" },
    { key: "actual_amount", label: "Amount" },
    { key: "requested_date", label: "Requested At" },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <StatusBadge status={row?.status} label={WITHDRAW_STATUS_LABEL[row?.status]} />
      ),
    },
    {
      key: "notes",
      label: "Notes",
      render: (row) => (
        <span style={{ maxWidth: 300, fontSize: "0.875rem", display: "block" }}>
          {row?.notes || ""}
        </span>
      ),
    },
    {
      key: "_action",
      label: "Action",
      render: (row) => (
        <div style={{ display: "flex", gap: 4 }}>
          {user?.role !== "MANAGER" && (
            <button
              type="button"
              aria-label="view details"
              onClick={(e) => { e.stopPropagation(); handleViewWithdrawRequest(row); }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 30,
                height: 30,
                borderRadius: 8,
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#c3c8cc",
                cursor: "pointer",
              }}
            >
              <Eye size={14} />
            </button>
          )}
          {row?.status === "APPROVED" && requestType !== "withdraw" && (
            <button
              type="button"
              aria-label="gift card redemption"
              onClick={(e) => {
                e.stopPropagation();
                setRedemptionRequest(row);
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 30,
                height: 30,
                borderRadius: 8,
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#c3c8cc",
                cursor: "pointer",
              }}
            >
              <ScanBarcode size={14} />
            </button>
          )}
        </div>
      ),
    },
  ];

  const handleStatusChange = (status, checked) => {
    setSelectedStatuses((prev) => (checked ? [...prev, status] : prev.filter((s) => s !== status)));
  };

  const statusList =
    requestType === "withdraw"
      ? ["PENDING", "APPROVED", "REJECTED", "ACTION_REQUIRED", "PROCESSING", "FAILED"]
      : ["PENDING_APPROVAL", "APPROVED", "REJECTED", "READY_FOR_REDEMPTION"];

  const handleSelectionChange = useCallback(
    (newSelection) => {
      if (JSON.stringify(selectedRows) !== JSON.stringify(newSelection)) {
        setSelectedRows(newSelection);
      }
    },
    [selectedRows]
  );

  const handleMakePayPalPayment = () => {
    if (selectedRows.length === 0) {
      showToast("Please select at least one request to make payment.", "error");
      return;
    }
    setLoading(true);
    const selectedIds = selectedRows.map((row) => row.id);
    dispatch(makePayPalPayment({ withdrawal_ids: selectedIds })).then((res) => {
      setLoading(false);
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        return;
      }
      showToast("Payment initiated successfully.", "success");
      getPaymentDetails(requestType);
      setResetSelectionFlag(true);
      setOpenPayPalPayment(false);
      setTimeout(() => setResetSelectionFlag(false), 100);
      setSelectedRows([]);
    });
  };

  const activeFilterCount = countActiveFiltersAndSort(sortDirection, selectedStatuses);
  const pagedRows = filteredData.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage);

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", width: "100%" }}>
      <div style={{ marginBottom: 20 }}>
        <PageHeader
          title="Wallet Withdraw Requests"
          subtitle="Keep track of all orders and wallet payout requests at a glance."
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 10,
          marginBottom: 20,
        }}
      >
        {/* Selection action bar */}
        {selectedRows?.length > 0 && (
          <div
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center",
              backgroundColor: "rgba(30,30,30,0.7)",
              borderRadius: 8,
              padding: "6px 12px",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <span style={{ color: "#ccc", fontSize: 13 }}>{selectedRows.length} selected</span>
            <button
              type="button"
              aria-label="Approve PayPal Payment"
              onClick={() => setOpenPayPalPayment(true)}
              style={{
                background: "rgba(76,175,80,0.1)",
                border: "1px solid #4CAF50",
                borderRadius: 6,
                padding: "3px 8px",
                cursor: "pointer",
                color: "#4CAF50",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Check size={16} />
            </button>
          </div>
        )}

        {/* Request type segmented control */}
        <div
          style={{
            display: "flex",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 10,
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          {[
            { value: "withdraw", label: "Withdrawal" },
            { value: "gift-card", label: "Gift Card" },
          ].map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => handleChangeRequestType(value)}
              style={{
                padding: "6px 16px",
                background: requestType === value ? "#00ADB5" : "transparent",
                color: requestType === value ? "#fff" : "#8b9296",
                border: "none",
                borderRight: value === "withdraw" ? "1px solid rgba(255,255,255,0.12)" : "none",
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 600,
                transition: "background 0.15s, color 0.15s",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {!isMobile && <RefreshButton onRefresh={() => getPaymentDetails(requestType)} spinning={loading} />}

        <SearchBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          setDebouncedTerm={setDebouncedSearch}
          placeholder="Search..."
        />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              <Filter size={14} />
              Filter & Sort
              {activeFilterCount > 0 && (
                <span
                  style={{
                    marginLeft: 4,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 16,
                    height: 16,
                    borderRadius: 8,
                    background: "#00ADB5",
                    color: "#000",
                    fontSize: 10,
                    fontWeight: 800,
                    padding: "0 4px",
                  }}
                >
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" style={{ minWidth: 220 }}>
            <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
            {statusList.map((status) => (
              <DropdownMenuCheckboxItem
                key={status}
                checked={selectedStatuses.includes(status)}
                onCheckedChange={(checked) => handleStatusChange(status, checked)}
                onSelect={(e) => e.preventDefault()}
              >
                {WITHDRAW_STATUS_LABEL[status] || status.replace(/_/g, " ")}
              </DropdownMenuCheckboxItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem active={sortDirection === "asc"} onClick={() => setSortDirection((prev) => prev === "asc" ? null : "asc")}>
              <ArrowUpNarrowWide size={14} style={{ marginRight: 8 }} />
              Sort Amount (Low to High)
            </DropdownMenuItem>
            <DropdownMenuItem active={sortDirection === "desc"} onClick={() => setSortDirection((prev) => prev === "desc" ? null : "desc")}>
              <ArrowDownWideNarrow size={14} style={{ marginRight: 8 }} />
              Sort Amount (High to Low)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <DataTable
        columns={columns}
        rows={pagedRows}
        totalRows={filteredData.length}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={(page) => setCurrentPage(page)}
        onRowsPerPageChange={(rpp) => { setRowsPerPage(rpp); setCurrentPage(0); }}
        loading={loading}
        isSelectable={user?.role === "SUPERADMIN"}
        selectableCondition={["isPayPalSelectable", "===", true]}
        onSelectionChange={handleSelectionChange}
        resetSelection={resetSelectionFlag}
      />

      {/* Bulk PayPal Payment Dialog */}
      <Dialog open={openPayPalPayment} onOpenChange={setOpenPayPalPayment}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Bulk PayPal Payment Approval</DialogTitle>
            <DialogDescription>
              You are about to approve {selectedRows.length} selected PayPal payment
              {selectedRows.length !== 1 ? "s" : ""}. Once approved, this action cannot be reversed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenPayPalPayment(false)}>
              Cancel
            </Button>
            <Button onClick={handleMakePayPalPayment}>Approve Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(viewRequest)} onOpenChange={(open) => !open && setViewRequest(null)}>
        <DialogContent style={{ maxWidth: 860, width: isMobile ? "calc(100vw - 16px)" : "90vw", maxHeight: "92vh" }}>
          <DialogHeader>
            <DialogTitle>{requestType === "withdraw" ? "Payment Details" : "Gift Card Details"}</DialogTitle>
            <DialogDescription>Review request details and process the available actions.</DialogDescription>
          </DialogHeader>
          {viewRequest && (
            <ViewWithdrawRequestPage
              dialogMode
              requestDetail={viewRequest}
              type={requestType}
              onUpdated={() => {
                setViewRequest(null);
                getPaymentDetails(requestType);
              }}
              onInitiatePayment={(payment) => {
                setViewRequest(null);
                setPaymentDetails(payment);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(paymentDetails)} onOpenChange={(open) => !open && setPaymentDetails(null)}>
        <DialogContent style={{ maxWidth: 560, width: isMobile ? "calc(100vw - 16px)" : "90vw", maxHeight: "92vh" }}>
          <DialogHeader>
            <DialogTitle>Approve Payment</DialogTitle>
            <DialogDescription>Enter payment details and upload a receipt if needed.</DialogDescription>
          </DialogHeader>
          {paymentDetails && (
            <UpdatePayoutFormPage
              dialogMode
              paymentDetails={paymentDetails}
              onCancel={() => setPaymentDetails(null)}
              onSuccess={() => {
                setPaymentDetails(null);
                getPaymentDetails(requestType);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(redemptionRequest)} onOpenChange={(open) => !open && setRedemptionRequest(null)}>
        <DialogContent style={{ maxWidth: 620, width: isMobile ? "calc(100vw - 16px)" : "90vw", maxHeight: "92vh" }}>
          <DialogHeader>
            <DialogTitle>Gift Card Redemption</DialogTitle>
            <DialogDescription>Add gift card code, PIN, and barcode image.</DialogDescription>
          </DialogHeader>
          {redemptionRequest && (
            <GiftCardRedemptionPage
              dialogMode
              requestDetail={redemptionRequest}
              type={requestType}
              onCancel={() => setRedemptionRequest(null)}
              onSuccess={() => {
                setRedemptionRequest(null);
                getPaymentDetails(requestType);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManagementWithdrawRequestPage;
